import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { CheckCircle2, CreditCard, Loader2, ShieldCheck, Clock, XCircle } from "lucide-react";
import InvoiceSheet from "@/components/InvoiceSheet";
import SignaturePad from "@/components/SignaturePad";
import { fetchPayInvoice, signPayInvoice, checkoutPayInvoice, formatApiError } from "@/lib/api";

const money = (value) => Number(value).toLocaleString("en-US", { style: "currency", currency: "USD" });
const panel = "border border-steel-border bg-steel-surface p-6 sm:p-8";
const button = "flex w-full items-center justify-center gap-2 px-4 py-3 text-sm font-semibold transition-colors disabled:opacity-40";

export default function PayInvoice() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const [invoice, setInvoice] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ready | notfound
  const [name, setName] = useState("");
  const [signature, setSignature] = useState(null);
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const busyRef = useRef(false);
  const pollRef = useRef(0);
  const cancelled = params.get("payment") === "cancelled";
  const returned = params.get("payment") === "return";

  const load = useCallback(async () => {
    try {
      const data = await fetchPayInvoice(token);
      setInvoice(data);
      setName((prev) => prev || data.signer_name || data.recipient_name || "");
      setStatus("ready");
      return data;
    } catch (e) {
      if (e?.response?.status === 404) setStatus("notfound");
      else setError(formatApiError(e));
      return null;
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  // After returning from Stripe, poll a few times until payment reflects.
  useEffect(() => {
    if (!returned || !invoice || invoice.status === "paid") return;
    if (pollRef.current >= 8) return;
    const t = setTimeout(async () => { pollRef.current += 1; await load(); }, 2500);
    return () => clearTimeout(t);
  }, [returned, invoice, load]);

  const run = async (fn) => {
    if (busyRef.current) return;
    busyRef.current = true; setBusy(true); setError("");
    try { await fn(); } catch (e) { setError(formatApiError(e)); }
    finally { busyRef.current = false; setBusy(false); }
  };

  const authorize = () => run(async () => {
    const updated = await signPayInvoice(token, { signer_name: name.trim(), signature_data: signature, consent });
    setInvoice(updated); setSignature(null);
  });

  const pay = () => run(async () => {
    const result = await checkoutPayInvoice(token);
    if (result.paid) return load();
    if (!result.checkout_url) throw new Error("Could not start checkout");
    window.location.assign(result.checkout_url);
  });

  if (status === "loading") {
    return <div className="mx-auto flex min-h-[70vh] max-w-lg items-center justify-center px-6" data-testid="pay-loading">
      <Loader2 className="h-8 w-8 animate-spin text-steel-brass" />
    </div>;
  }

  if (status === "notfound") {
    return <div className="mx-auto flex min-h-[70vh] max-w-lg flex-col items-center justify-center px-6 py-24 text-center" data-testid="pay-notfound">
      <XCircle className="h-12 w-12 text-steel-muted" />
      <h1 className="mt-6 font-display text-3xl font-medium text-steel-text">Payment link not found</h1>
      <p className="mt-3 text-sm text-steel-secondary">This link is invalid or has been revoked. Please contact Steele Industries for a new one.</p>
    </div>;
  }

  const paid = invoice.status === "paid";
  const signed = Boolean(invoice.signed_at);
  const expired = invoice.link_expired;

  return (
    <div className="mx-auto max-w-5xl px-6 py-16 sm:py-24" data-testid="pay-invoice-page">
      <div className="mb-8">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-steel-muted">Steele Industries</p>
        <h1 className="mt-2 font-display text-3xl font-medium text-steel-text sm:text-4xl">
          {paid ? "Payment received" : "Review & pay your invoice"}
        </h1>
      </div>
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
        <div className="min-w-0 lg:col-span-7">
          <InvoiceSheet invoice={invoice} />
        </div>
        <div className="min-w-0 lg:col-span-5">
          <section className={panel} data-testid="pay-panel">
            <div className="flex items-center gap-2 text-steel-brass">
              <ShieldCheck className="h-5 w-5" />
              <h2 className="font-display text-lg">Secure payment</h2>
            </div>
            <div className="mt-4 flex items-baseline justify-between" data-testid="pay-total">
              <span className="text-sm text-steel-muted">{paid ? "Total paid" : "Amount due"}</span>
              <strong className="font-display text-2xl text-steel-text">{money(invoice.total)}</strong>
            </div>

            {error && <p role="alert" data-testid="pay-error" className="mt-4 break-words text-sm text-red-400">{error}</p>}

            {paid ? (
              <div className="mt-6 space-y-3" data-testid="pay-paid">
                <p className="flex items-center gap-2 text-sm font-medium text-emerald-400">
                  <CheckCircle2 className="h-5 w-5" /> Paid in full · Balance $0.00
                </p>
                <p className="text-sm text-steel-secondary">A paid receipt has been emailed to {invoice.recipient_email}. Thank you.</p>
              </div>
            ) : expired ? (
              <div className="mt-6 space-y-3" data-testid="pay-expired">
                <p className="flex items-center gap-2 text-sm font-medium text-amber-400">
                  <Clock className="h-5 w-5" /> This payment link has expired
                </p>
                <p className="text-sm text-steel-secondary">Please contact Steele Industries and we'll send you a fresh link.</p>
              </div>
            ) : !signed ? (
              <div className="mt-6 space-y-4" data-testid="pay-sign">
                <label className="block text-sm text-steel-secondary" htmlFor="pay-signer-name">Your full name</label>
                <input id="pay-signer-name" data-testid="pay-signer-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={120}
                  className="w-full min-w-0 border border-steel-border bg-steel-elevated p-3 text-base outline-none focus:border-steel-brass" />
                <SignaturePad onChange={setSignature} />
                <label className="flex items-start gap-3 text-sm leading-relaxed text-steel-secondary">
                  <input type="checkbox" data-testid="pay-consent" checked={consent} onChange={(e) => setConsent(e.target.checked)}
                    className="mt-1 h-5 w-5 shrink-0 accent-purple-500" />
                  <span data-testid="pay-authorization-text">I authorize Steele Industries to charge {money(invoice.total)} for invoice {invoice.number}.</span>
                </label>
                <button data-testid="pay-sign-btn" disabled={busy || !signature || !consent || name.trim().length < 2} onClick={authorize}
                  className={`${button} bg-steel-accent text-white hover:bg-steel-accentHover`}>
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />} Authorize &amp; continue
                </button>
              </div>
            ) : (
              <div className="mt-6 space-y-4" data-testid="pay-checkout">
                <p data-testid="pay-signed" className="text-sm text-emerald-400">Signature saved · {invoice.signer_name}</p>
                {cancelled && <p data-testid="pay-cancelled" className="text-sm text-steel-muted">Checkout was closed — no charge was made. You can try again below.</p>}
                {invoice.payment_state === "processing" ? (
                  <p data-testid="pay-processing" className="text-sm text-steel-secondary">Your payment is processing. This page will update automatically.</p>
                ) : (
                  <button data-testid="pay-checkout-btn" disabled={busy} onClick={pay}
                    className={`${button} bg-steel-accent text-white hover:bg-steel-accentHover`}>
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}
                    {invoice.payment_state === "checkout" ? "Resume secure checkout" : "Pay by card"}
                  </button>
                )}
                <button data-testid="pay-refresh-btn" disabled={busy} onClick={() => run(load)}
                  className={`${button} border border-steel-border text-steel-secondary hover:border-steel-brass`}>
                  <Loader2 className={`h-4 w-4 ${busy ? "animate-spin" : "hidden"}`} /> Check payment status
                </button>
              </div>
            )}
            <p className="mt-6 text-xs leading-relaxed text-steel-muted">Payments are processed securely by Stripe. Card details are never stored by Steele Industries.</p>
          </section>
        </div>
      </div>
    </div>
  );
}
